import {useEffect, useEffectEvent, useRef, useState} from 'react';
import {assert} from 'assert-ts';
import {Concept, type EntityMainType, type EntitySubType} from 'types';
import {HTTP_STATUS_ABORTED, HttpError} from 'api/http/types';
import type {DataLoadStatus, GetTokens} from 'api/types';
import {searchSeriesCount} from 'api/searchSeriesCount';
import {useGetTokens} from 'services/auth';
import type {SearchMainEntityType} from '../types';
import {searchAgentsCount} from '../../../api/searchAgentsCount';
import {trimSearchQuery} from '../functions';

export type SearchCountResult<T = EntitySubType> = Map<T, number | undefined>;

export const EMPTY_COUNT_RESULT: SearchCountResult = new Map();
export const DEFAULT_COUNT_RESULT: DataLoadStatus<SearchCountResult> = {
  status: 'Loaded',
  data: EMPTY_COUNT_RESULT,
};

type SearchEntityCountFunc = (
  search: string,
  getTokens: GetTokens,
  signal: AbortSignal,
) => Promise<Map<EntitySubType, number>>;

const searchEntityMap: {[type in SearchMainEntityType]: SearchEntityCountFunc} =
  {
    [Concept.agent]: (
      search: string,
      getTokens: GetTokens,
      signal: AbortSignal,
    ) => searchAgentsCount(search, getTokens, signal),
    [Concept.collection]: (
      search: string,
      getTokens: GetTokens,
      signal: AbortSignal,
    ) => searchSeriesCount(search, getTokens, signal),
    [Concept.work]: (
      // Work has no subtypes
      _search: string,
      _getTokens: GetTokens,
      _signal: AbortSignal,
    ) => Promise.resolve(new Map()),
    [Concept.prize]: (
      // Prize has no subtypes
      _search: string,
      _getTokens: GetTokens,
      _signal: AbortSignal,
    ) => Promise.resolve(new Map()),
  };

export const useEntitySearchCount = (
  searchQuery: string,
  entityType: SearchMainEntityType,
): DataLoadStatus<SearchCountResult> => {
  const [result, setResult] =
    useState<DataLoadStatus<SearchCountResult>>(DEFAULT_COUNT_RESULT);
  const onSetResult = useEffectEvent(setResult);
  const searchQueryRef = useRef<string | undefined>(undefined);
  const entityTypeRef = useRef<EntityMainType>(entityType);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const getTokens = useGetTokens();

  // When search query or entity type/subtype is changed
  // => abort any ongoing search, search with new criteria
  useEffect(() => {
    if (
      searchQuery === searchQueryRef.current &&
      entityType === entityTypeRef.current
    ) {
      return;
    }

    // Abort any ongoing call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }

    if (!searchQuery) {
      onSetResult(DEFAULT_COUNT_RESULT);
      return;
    }

    if (entityType === entityTypeRef.current) {
      onSetResult(prev => ({
        ...prev,
        status: 'Loading',
      }));
    }

    abortControllerRef.current = new AbortController();
    searchQueryRef.current = searchQuery;
    entityTypeRef.current = entityType;

    const trimmedQuery = trimSearchQuery(searchQuery);
    assert(
      searchEntityMap[entityType],
      'searchEntityMap[entityType] expected',
      {entityType},
    )(trimmedQuery, getTokens, abortControllerRef.current.signal)
      .then(data => {
        onSetResult({status: 'Loaded', data});
      })
      .catch(error => {
        if (
          error instanceof HttpError &&
          error.status === HTTP_STATUS_ABORTED
        ) {
          // Caused by abort above, leave state as is - to be updated by next call
          return;
        }

        console.error('useEntitySearchCount: search failed', error);
        onSetResult({
          status: 'Failed',
          error,
        });
      });
  }, [getTokens, searchQuery, entityType]);

  return result;
};
